import { ProductsConfigurationData } from '@app/models/lead-resume';

/**
 * Utility para mapear datos del Lead Resume a las estructuras del Store de NgRx
 * Sigue las mejores prácticas de Angular 17
 */

/**
 * Mapea los datos del propietario (PolicyHolder) desde Lead Resume
 * ✅ IMPORTANTE: Solo incluye campos que tienen valor para evitar sobrescribir datos del usuario con strings vacíos
 */
export function mapToPolicyHolder(data: ProductsConfigurationData) {
  // Construir objeto base con campos siempre presentes
  const policyHolder: any = {
    DocumentType: data.DocumentType || 'C',
    CountryOfBirth: 'Colombia',
    PersonalDataAuthorization: true,
    PersonalDataVersion: new Date().toLocaleDateString('es-CO'),
    PersonalDataLastUpdated: new Date().toLocaleDateString('es-CO')
  };

  // ✅ Solo agregar campos si tienen valor (evita sobrescribir con strings vacíos)
  if (data.FirstName) policyHolder.FirstName = data.FirstName;
  if (data.LastName) policyHolder.LastName = data.LastName;
  if (data.DocumentNumber) policyHolder.DocumentNumber = data.DocumentNumber;
  if (data.ExpeditionDate) policyHolder.ExpeditionDate = data.ExpeditionDate;
  if (data.Mail) policyHolder.Mail = data.Mail;
  if (data.MobilePhone) policyHolder.MobilePhone = data.MobilePhone;

  // Solo incluir SecondName si tiene valor
  if (data.SecondName && data.SecondName.trim() !== '') {
    policyHolder.SecondName = data.SecondName;
  }

  // Solo incluir SecondLastName si tiene valor
  if (data.SecondLastName && data.SecondLastName.trim() !== '') {
    policyHolder.SecondLastName = data.SecondLastName;
  }

  return policyHolder;
}

/**
 * Mapea los datos del inquilino (Tenant) desde Lead Resume
 * ✅ IMPORTANTE: Solo incluye campos que tienen valor para evitar sobrescribir datos del usuario con strings vacíos
 */
export function mapToTenant(data: ProductsConfigurationData) {
  if (!data.Tenant) {
    return null;
  }

  // Construir objeto solo con campos que tienen valor
  const tenant: any = {
    DocumentType: data.Tenant.DocumentType || 'C',
    PersonalDataAuthorization: true,
    PersonalDataVersion: new Date().toLocaleDateString('es-CO'),
    PersonalDataLastUpdated: new Date().toLocaleDateString('es-CO')
  };

  // ✅ Solo agregar campos si tienen valor (evita sobrescribir con strings vacíos)
  if (data.Tenant.FirstName) tenant.FirstName = data.Tenant.FirstName;
  if (data.Tenant.DocumentNumber) tenant.DocumentNumber = data.Tenant.DocumentNumber;
  if (data.Tenant.Mail) tenant.Mail = data.Tenant.Mail;

  return tenant;
}

/**
 * Mapea los datos de la propiedad (Property) desde Lead Resume
 * ✅ IMPORTANTE: Solo incluye campos que tienen valor para evitar sobrescribir datos del usuario con strings vacíos
 */
export function mapToProperty(data: ProductsConfigurationData) {
  if (!data.Property) {
    return null;
  }

  // Construir objeto solo con campos que tienen valor
  const property: any = {
    RentalFee: typeof data.Property.RentalFee === 'number'
      ? data.Property.RentalFee
      : (typeof data.Property.RentalFee === 'string' ? Number(data.Property.RentalFee) : 0),
    AdministrationValue: typeof data.Property.AdministrationValue === 'number'
      ? data.Property.AdministrationValue
      : (typeof data.Property.AdministrationValue === 'string' ? Number(data.Property.AdministrationValue) : 0),
    UseProperty: data.Property.UseProperty || 'Residencial'
  };

  // ✅ Solo agregar campos si tienen valor (evita sobrescribir con strings vacíos)
  if (data.Property.Department) property.Department = data.Property.Department;
  if (data.Property.CityProperty) property.CityProperty = data.Property.CityProperty;
  if (data.Property.CityCode) property.CityCode = data.Property.CityCode;
  if (data.Property.DepartmentCode) property.DepartmentCode = data.Property.DepartmentCode;
  if (data.Property.Code) property.Code = data.Property.Code;
  if (data.Property.PropertyAddress) property.PropertyAddress = data.Property.PropertyAddress;
  if (data.Property.RealEstateLicensePlate) property.RealEstateLicensePlate = data.Property.RealEstateLicensePlate;
  if (data.Property.TemporaryContracted) property.TemporaryContracted = data.Property.TemporaryContracted;
  if (data.Property.ContractEndDate) property.ContractEndDate = data.Property.ContractEndDate;

  // ✅ CRÍTICO: Solo incluir ContractStartDate si tiene valor para NO sobrescribir el valor del usuario
  if (data.Property.ContractStartDate) property.ContractStartDate = data.Property.ContractStartDate;

  return property;
}

/**
 * Mapea el código de producto si existe
 */
export function mapToProductCode(data: ProductsConfigurationData) {
  return data.ProductCode || null;
}

/**
 * Mapea todos los datos disponibles del Lead Resume
 * Retorna un objeto con las keys que NgRx espera
 */
export interface LeadResumeStoreData {
  PolicyHolder: ReturnType<typeof mapToPolicyHolder> | null;
  Tenant: ReturnType<typeof mapToTenant> | null;
  Property: ReturnType<typeof mapToProperty> | null;
  ProductCode: string | null;
}

export function mapLeadResumeToStore(data: ProductsConfigurationData): LeadResumeStoreData {
  return {
    PolicyHolder: mapToPolicyHolder(data),
    Tenant: mapToTenant(data),
    Property: mapToProperty(data),
    ProductCode: mapToProductCode(data)
  };
}

/**
 * Verifica qué datos están disponibles en el Lead Resume
 */
export interface LeadResumeAvailability {
  hasPolicyHolder: boolean;
  hasTenant: boolean;
  hasProperty: boolean;
  hasProductCode: boolean;
}

export function checkLeadResumeAvailability(data: ProductsConfigurationData): LeadResumeAvailability {
  return {
    hasPolicyHolder: !!(data.FirstName && data.LastName && data.DocumentNumber),
    hasTenant: !!(data.Tenant?.DocumentNumber || data.Tenant?.FirstName || data.Tenant?.Mail),
    hasProperty: !!(data.Property),
    hasProductCode: !!data.ProductCode
  };
}
